import { useRouter } from "next/router";
import { useLayoutEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSetRecoilState } from "recoil";
import { authFormState } from "../../Atoms/auth/AuthFormState";
import { useToast } from "@/components/ui/use-toast";
import { Eye, EyeOff, Loader2, Mail, Lock, ArrowRight } from "lucide-react";
import { NextPage } from "next";
import { signOut } from "next-auth/react";
import { useTranslation } from "next-i18next";

import Disclaimer from "../Desclaimer";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import GoogleAnalytics from "../GA";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { usePatternUniverses } from "@/contexts/PatternUniverse";
import { useUserContext } from "@/contexts/userContext";
import { usePostLogin, usePostVerify, usePostGoogleAuth } from "@/api/userApi";
import GoogleOAuthButton from "./GoogleOAuthButton";
import Loader from "../Loader";
import { AnalyticsEvents, useAnalytics } from "../hooks/useAnalytics";
import Link from "next/link";
import { useAppDetails } from "@/contexts/AppDetailsContext";
interface IFormValues {
  email: string;
  password: string;
}

const LoginCard: NextPage = (props: any) => {
  const { toast } = useToast();
  const router = useRouter();
  const { t, i18n } = useTranslation("common-page");
  const setFormState = useSetRecoilState(authFormState);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { setSubscriptionData, setMetaData, getUserType } = useSubscription();
  const { refetchScannerStrategies, refetchUniverse } = usePatternUniverses();

  const getDisclaimerTexts = () => {
    const lang = i18n.language || "en";
    const noticeText = t("notice");
    
    switch (lang.slice(0, 2)) {
      case "es":
        return { plain: "Al continuar acepta nuestro ", link: `comercio de riesgos ${noticeText}` };
      case "pt":
        return { plain: "Ao continuar você concorda com nossa ", link: `negociação de risco ${noticeText}` };
      case "de":
        return { plain: "Durch die Fortsetzung stimmen Sie unserem ", link: `Risikohandel ${noticeText}` };
      case "it":
        return { plain: "Continuando accetti il nostro ", link: `trading di rischio ${noticeText}` };
      case "zh":
        return { plain: "继续你同意我们的", link: `风险交易${noticeText}` };
      case "ja":
        return { plain: "引き続き、当社の", link: `リスク取引に合意${noticeText}` };
      case "th":
        return { plain: "โดยให้คุณตกลงที่จะ", link: `แลกเปลี่ยนความเสี่ยงของเรา${noticeText}` };
      case "id":
        return { plain: "Dengan melanjutkan Anda setuju untuk kami ", link: `risiko perdagangan ${noticeText}` };
      case "vi":
        return { plain: "Bằng cách tiếp tục thỏa thuận ", link: `giao dịch rủi ro ${noticeText}` };
      case "ms":
        return { plain: "Dengan melanjutkan Anda setuju untuk ", link: `perdagangan risiko kami ${noticeText}` };
      default:
        return {
          plain: "By continuing you agree to our ",
          link: `risk trading ${noticeText}`
        };
    }
  };
  const disclaimerText = getDisclaimerTexts();
  const { user, setUser, refreshConnectedBrokers } = useUserContext();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IFormValues>();
  const { mutateAsync: login, isPending: loading } = usePostLogin();
  const { mutateAsync: googleAuth, isPending: googleLoading } = usePostGoogleAuth();
  const { mutateAsync: verify, isPending: sessionLoading } = usePostVerify();
  const { trackEvent } = useAnalytics();
  const { details } = useAppDetails();
  const togglePasswordVisibility = () => {
    if (showPassword) {
      setShowPassword(false);
    } else {
      setShowPassword(true);
    }
  };
  function isValidJSON(data: any) {
    try {
      JSON.parse(data); // Attempt to parse the string
      return true; // If successful, it's valid JSON
    } catch (e) {
      return false; // If an error occurs, it's invalid JSON
    }
  }
  const handleAuthSuccess = (data: any) => {
    let subscription_status = "free";

    if (isValidJSON(data?.subscription)) {
      subscription_status = "pro";
    } else {
      subscription_status = data?.subscription;
    }
    setSubscriptionData(subscription_status);
    setMetaData(data?.user?.meta_data || null);
    getUserType(data);
    setUser(data);
    localStorage.setItem("userAuthenticated", "true");
    refetchScannerStrategies(true, data?.user_id);
    refetchUniverse(true, data?.user_id);
    refreshConnectedBrokers();
    router.query.locale = data?.preference?.language || "en";

    if (data?.subscription === "inactive") {
      return router.push(
        `/subscription?locale=${router.query.locale || "en"}`,
      );
    }

    if (router.query.redirect) {
      return router.push(router.query.redirect as string);
    }

    return router.push(`/welcome-tour?locale=${router.query.locale || "en"}`);
  };

  const onSubmit = async (formData: any) => {
    try {
      const formD = new FormData();
      formD.append("email", (formData.email as string) || "");
      formD.append("password", (formData.password as string) || "");

      const res = await login({ formData: formD });

      if (res.status !== 200) throw new Error("Login failed");
      handleAuthSuccess(res.data);
    } catch (e: any) {
      const errorMsg =
        e?.response?.data?.detail ||
        e?.response?.data?.error ||
        "Incorrect email or password";
      setError(errorMsg);
      toast({ title: errorMsg, variant: "destructive" });
    }
  };

  const onGoogleSuccess = async (idToken: string) => {
    try {
      const res = await googleAuth({ idToken });
      if (res.status !== 200) throw new Error("Google authentication failed");
      handleAuthSuccess(res.data);
    } catch (e: any) {
      const errorMsg =
        e?.response?.data?.detail ||
        e?.response?.data?.error ||
        "Google authentication failed. Please try again.";
      setError(errorMsg);
      toast({ title: errorMsg, variant: "destructive" });
    }
  };

  const verifySession = async () => {
    try {
      const userAuthenticated = localStorage.getItem("userAuthenticated");

      if (user?.user_id && userAuthenticated === "true") {
        const res = await verify();

        if (res.statusText === "OK") {
          router.query.locale = user?.preference.language || "en";

          if (router.query.redirect) {
            await router.push(router?.query?.redirect?.toString());
            return;
          }

          await router.push(
            `/welcome-tour?locale=${
              user?.preference?.language || router.query.locale || "en"
            }`,
          );
        }
      }
    } catch (e: any) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const isSubscriptionError =
        status === 403 ||
        detail === "Request free access to evaluate or upgrade to the Pro plan for full access.";
      if (isSubscriptionError) {
        router.query.locale = user?.preference?.language || "en";
        await router.push(
          `/welcome-tour?locale=${
            user?.preference?.language || router.query.locale || "en"
          }`,
        );
      } else {
        setUser(null);
        Object.keys(localStorage).forEach((key) => {
          if (key !== "app-details" && !key.startsWith("welcome_pm_modal")) {
            localStorage.removeItem(key);
          }
        });
        signOut({ redirect: false });
      }
    }
  };

  useLayoutEffect(() => {
    verifySession();
  }, []);

  if (sessionLoading)
    return (
      <div className="fixed h-screen w-screen bg-[hsl(var(--background))] z-50 top-0 left-0 flex justify-center items-center">
        <Loader />
      </div>
    );
  return (
    <>
      <GoogleAnalytics GA_TRACKING_ID={"G-NRPKERR6KT"} />
      <div className="max-w-[500px] mb-5">
        <GoogleOAuthButton
          onSuccess={onGoogleSuccess}
          isLoading={googleLoading}
        />

        <div className="relative my-4 flex items-center justify-center">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border/60" />
          </div>
          <div className="relative bg-background px-3 text-xs uppercase text-muted-foreground font-medium">
            {t("Or continue with email")}
          </div>
        </div>

        <form
          method="post"
          autoComplete="false"
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col space-y-4  loginForm"
        >
          <div className="flex flex-col space-y-1.5 text-left">
            <label className="text-sm font-semibold text-foreground/90">
              {t("Email")}
            </label>
            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                {...register("email", {
                  required: true,
                  pattern: {
                    value: /\S+@\S+\.\S+/,
                    message: t("Please enter a valid email address."),
                  },
                })}
                type="text"
                autoComplete="off"
                onKeyUp={() => setError("")}
                placeholder={t("Enter your email")}
                name="email"
                className="pl-10 h-11 border-border/80 focus-visible:ring-1 focus-visible:ring-primary rounded-lg placeholder:text-muted-foreground/60 placeholder:font-medium bg-background/50"
              />
            </div>
            {errors?.email && (
              <div className="danger text-left">
                {errors.email?.type === "required" && (
                  <p className="text-destructive text-sm">
                    {t("E-mail is required.")}
                  </p>
                )}
                {errors.email?.type === "pattern" && (
                  <p className="text-destructive text-sm">
                    {errors.email?.message}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col space-y-1.5 text-left">
            <label className="text-sm font-semibold text-foreground/90">
              {t("Password")}
            </label>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                {...register("password", { required: true })}
                type={showPassword ? "text" : "password"}
                autoComplete="off"
                placeholder={t("Enter your password")}
                onKeyUp={() => setError("")}
                name="password"
                className="pl-10 pr-10 h-11 border-border/80 focus-visible:ring-1 focus-visible:ring-primary rounded-lg placeholder:text-muted-foreground/60 placeholder:font-medium bg-background/50"
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 cursor-pointer">
                {showPassword ? (
                  <EyeOff
                    className="h-4 w-4"
                    onClick={togglePasswordVisibility}
                  />
                ) : (
                  <Eye className="h-4 w-4" onClick={togglePasswordVisibility} />
                )}
              </div>
            </div>
            {errors?.password && (
              <div className="danger text-left">
                {errors.password?.type === "required" && (
                  <p className="text-destructive text-sm">
                    {t("Password is required")}
                  </p>
                )}
              </div>
            )}
            <div className="flex justify-end text-xs !mt-3">
              <span
                className="cursor-pointer text-[--accent-primary] hover:text-[--accent-primary]/80 transition-colors font-medium underline underline-offset-4"
                onClick={() => setFormState({ view: "resetPassword" })}
              >
                {t("Forgot password?")}
              </span>
            </div>
          </div>

          {error && <span className="text-destructive text-sm">{error}</span>}

          <div className="text-center font-bold">
            <Button type="submit" className="w-full">
              {t("Sign In")}

              {loading ? <Loader2 className="ml-2 h-5 w-5 animate-spin" /> : ""}
            </Button>
          </div>
        </form>
      </div>
      <div className="flex flex-col !mt-2">
        <div className="flex flex-row justify-center items-center text-newGreen  text-xs"></div>
      </div>
      <div className="space-y-4">
        <div className="text-center text-sm text-muted-foreground">
          <div className="flex items-center flex-col justify-center gap-1">
            <span>{t("Don't have an account?")}</span>{" "}
            <Link
              href={`/create-new-account?locale=${router.query.locale || "en"}`}
              onClick={() => {
                trackEvent({
                  event_name: AnalyticsEvents.REGISTRATION_CLICKED,
                  params: { source: "login_page" },
                });
              }}
              className="inline-flex items-center gap-1.5 font-medium text-blue-500 hover:text-blue-600 transition-colors"
            >
              <span>{t("Create your free account")}</span>
              <span className="inline-flex items-center justify-center border border-blue-500 rounded-full p-0.5">
                <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </Link>
          </div>
        </div>
        <hr className="border-t border-border/40 my-1" />

        <div className="text-center text-[11px] text-muted-foreground/60">
          <div className="text-[11px] text-muted-foreground/60 leading-normal max-w-sm mx-auto">
            {disclaimerText.plain} <Disclaimer triggerText={disclaimerText.link} />
          </div>
          <span>{t("Having trouble logging in? Drop us a message-").replace(/-$/, ":")}</span>{" "}
          <Button
            variant="link"
            className="p-0 h-auto text-[11px] text-[--accent-primary] hover:text-[--accent-primary]/80 transition-colors font-medium underline inline-flex"
            onClick={() => {
              window.location.href = `mailto:${details?.supportEmail}?subject=Request%20Assistance&body=Hi%20Team,%0DI%20need%20assistance.%20My%20contact%20email%20is%20{enter%20your%20email%20address}%0D%0D%0D%0D`;
            }}
          >
            {details?.supportEmail}
          </Button>
        </div>
      </div>
    </>
  );
};

export default LoginCard;
